import readXlsxFile from "read-excel-file";
import { FileComment } from "./processCSVFile";
import { showErrorToast } from "../../../../../components/common/ShowToast";

export interface ErrorComment {
  id: number;
  first_name: string;
  last_name: string;
  comment: string;
  email: string;
  phone_number: string;
  unknownFieldsArray: unknown[] | undefined;
}

export const processExcelComments = async (
  stream_id: string | undefined,
  file: File,
  fieldsMapped: {
    last_name: string;
    first_name: string;
    email: string;
    phone_number: string;
    comment: string;
  }
) => {
  try {
    const commentsRows = (await readXlsxFile(file)) as unknown[][];
    const xlsxFileHeadings = commentsRows[0];
    const correctCommentLineHeadings = xlsxFileHeadings.length;
    const fieldsMappedKeys = Object.keys(
      fieldsMapped
    ) as (keyof typeof fieldsMapped)[];
    const fieldsIndexMap: {
      field: string;
      mappedString: string;
      index: number;
    }[] = [];

    for (let i = 0; i < fieldsMappedKeys.length; i++) {
      const currentKey = fieldsMappedKeys[i];
      const currentValue = fieldsMapped[currentKey];
      const headingIndexInFile = xlsxFileHeadings.indexOf(currentValue);
      fieldsIndexMap.unshift({
        field: currentKey,
        mappedString: currentValue,
        index: headingIndexInFile,
      });
    }

    const errorComments = commentsRows.filter(
      (comment) => comment.length !== correctCommentLineHeadings
    );
    const correctComments = commentsRows.filter(
      (comment) => comment.length === correctCommentLineHeadings
    );

    const cellToString = (cell: unknown): string => {
      if (cell === null || cell === undefined) return "";
      if (cell instanceof Date) return cell.toISOString();
      return String(cell);
    };

    const errorCommentsMapped: ErrorComment[] = errorComments.map(
      (errorComment, index) => {
        const extractedIndex = extractIndexs(fieldsIndexMap);

        if (errorComment.length > correctCommentLineHeadings) {
          const unknownFieldsArray = errorComment.slice(
            correctCommentLineHeadings
          );
          return {
            id: index,
            first_name: cellToString(
              errorComment[extractedIndex.firstNameFileIndex]
            ),
            last_name: cellToString(
              errorComment[extractedIndex.lastNameFileIndex]
            ),
            comment: cellToString(
              errorComment[extractedIndex.commentFileIndex]
            ),
            email: cellToString(errorComment[extractedIndex.emailFileIndex]),
            phone_number: cellToString(
              errorComment[extractedIndex.phoneNumberFileIndex]
            ),
            unknownFieldsArray: [...unknownFieldsArray],
          };
        }

        return {
          id: index,
          first_name: cellToString(
            errorComment[extractedIndex.firstNameFileIndex]
          ),
          last_name: cellToString(
            errorComment[extractedIndex.lastNameFileIndex]
          ),
          comment: cellToString(errorComment[extractedIndex.commentFileIndex]),
          email: cellToString(errorComment[extractedIndex.emailFileIndex]),
          phone_number: cellToString(
            errorComment[extractedIndex.phoneNumberFileIndex]
          ),
          unknownFieldsArray: undefined,
        };
      }
    );

    const comments: FileComment[] = correctComments.map((comment, index) => {
      const firstNameFileIndex = fieldsIndexMap.filter(
        (obj) => obj.field == "first_name"
      )[0].index;
      const lastNameFileIndex = fieldsIndexMap.filter(
        (obj) => obj.field == "last_name"
      )[0].index;
      const commentFileIndex = fieldsIndexMap.filter(
        (obj) => obj.field == "comment"
      )[0].index;
      const emailFileIndex = fieldsIndexMap.filter(
        (obj) => obj.field == "email"
      )[0].index;
      const phoneNumberFileIndex = fieldsIndexMap.filter(
        (obj) => obj.field == "phone_number"
      )[0].index;
      return {
        id: index,
        first_name: cellToString(comment[firstNameFileIndex]),
        last_name: cellToString(comment[lastNameFileIndex]),
        comment: cellToString(comment[commentFileIndex]),
        email: cellToString(comment[emailFileIndex]),
        phone_number: cellToString(comment[phoneNumberFileIndex]),
      };
    });
    comments.shift();
    return {
      comments,
      errorCommentsMapped,
      displayMode: "confirm-data",
      message: "processed",
      errorOccured: false,
    };
  } catch (error) {
    return {
      displayMode: "",
      comments: [],
      message:
        "Failed to process the file.Please make sure it is not corrupted",
      errorOccured: true,
    };
  }
};

export const extractExcelFileHeading = async (
  file: File
): Promise<string[] | string> => {
  try {
    const commentsRows = await readXlsxFile(file);
    const xlsxFileHeadings = commentsRows[0];
    const parsedToString = xlsxFileHeadings.map((heading) => `${heading}`);
    return parsedToString;
  } catch {
    showErrorToast(
      "Failed to extract excel file headings.Please try selecting file again"
    );
    return "Error occured";
  }
};

const extractIndexs = (
  fieldsIndexMap: {
    field: string;
    mappedString: string;
    index: number;
  }[]
): {
  firstNameFileIndex: number;
  lastNameFileIndex: number;
  commentFileIndex: number;
  emailFileIndex: number;
  phoneNumberFileIndex: number;
} => {
  const firstNameFileIndex = fieldsIndexMap.filter(
    (obj) => obj.field == "first_name"
  )[0].index;
  const lastNameFileIndex = fieldsIndexMap.filter(
    (obj) => obj.field == "last_name"
  )[0].index;
  const commentFileIndex = fieldsIndexMap.filter(
    (obj) => obj.field == "comment"
  )[0].index;
  const emailFileIndex = fieldsIndexMap.filter((obj) => obj.field == "email")[0]
    .index;
  const phoneNumberFileIndex = fieldsIndexMap.filter(
    (obj) => obj.field == "phone_number"
  )[0].index;

  return {
    firstNameFileIndex,
    lastNameFileIndex,
    commentFileIndex,
    emailFileIndex,
    phoneNumberFileIndex,
  };
};
